import { faSolidMapCirclePlus } from '@awesome.me/kit-09b03e5df4/icons/kit/custom';
import { css } from '@emotion/react';
import { type FC } from 'react';
import { AlertComponent } from '~/_shared/baseComponents/alert';
import {
  getRoundButtonSizePx, RoundButtonComponent, RoundButtonSize,
} from '~/_shared/baseComponents/buttons/roundButton/roundButton.component';
import { RoundButtonStyle } from '~/_shared/baseComponents/buttons/roundButton/roundButton.styles';
import { OverlayLoaderComponent } from '~/_shared/components/overlay/overlayLoader.component';
import { OverlayWrapperComponent } from '~/_shared/components/overlay/overlayWrapper.component';
import {
  ScrollBarComponent, ScrollbarType,
} from '~/_shared/components/scrollbar/scrollbar.component';
import { type Pagination } from '~/_shared/types/pagination/pagination';
import { MobileEmptyMapListingComponent } from '~/map/listing/mobileEmptyMapListingState.component';
import { type ShareMapProps } from '~/map/share/shareMap.container';
import { type MapListingItem } from './item/mapListingItem';
import { MobileTableDataComponent } from './mobileTableData/mobileTableData.component';

const scrollBarStyle = css({
  minHeight: 'unset',
});

const floatingButtonVerticalOffset = 4;
const floatingButtonSize = getRoundButtonSizePx(RoundButtonSize.XLarge);

const containerStyle = css({
  '&:before': {
    content: '""',
    clear: 'both',
    display: 'table',
  },
  paddingBottom: floatingButtonSize + 2 * floatingButtonVerticalOffset,
});

const emptyStyle = css({
  alignItems: 'center',
  display: 'flex',
  height: '100%',
  justifyContent: 'center',
});

const alertWrapperStyle = css({
  margin: 10,
});

const spinnerStyle = css({
  position: 'fixed',
});

const relativeWrapperStyle = css({
  flex: '1 1 auto',
  minHeight: 0,
  position: 'relative',
});

const floatingCreateMapButtonStyle = css({
  position: 'absolute',
  bottom: floatingButtonVerticalOffset,
  right: 20,
  zIndex: 100,
});

type MobileMapListingProps = Readonly<{
  errorMessage: string | null;
  isMapListingDataLoading: boolean;
  pagination: Pagination | null;
  processedItems: ReadonlyArray<MapListingItem>;
  selectedMapIds: ReadonlyArray<number>;
  selectedSnapshotIds: ReadonlySet<number>;
  toggledMapIds: ReadonlyArray<number>;

  onCreateMapButtonClick: () => void;
  onMapShareSettingsClick: (props: ShareMapProps) => void;
  onPageChange: (page: number) => void;
  toggleSubMapVisibility: (mapId: number) => void;
}>;

export const MobileMapListingComponent: FC<MobileMapListingProps> = (props) => {
  const hasContent = props.errorMessage || props.isMapListingDataLoading || props.processedItems.length > 0;

  return (
    <div css={relativeWrapperStyle}>
      {hasContent ? (
        <ScrollBarComponent css={scrollBarStyle} type={ScrollbarType.Vertical}>
          <div css={containerStyle}>
            <OverlayWrapperComponent>
              <MobileTableDataComponent
                data={props.processedItems}
                onShowSubMapsToggle={props.toggleSubMapVisibility}
                toggledMapIds={props.toggledMapIds}
                pagination={props.pagination}
                onPageChange={props.onPageChange}
                isMapListingDataLoading={props.isMapListingDataLoading}
                onMapShareSettingsClick={props.onMapShareSettingsClick}
              />

              {props.errorMessage && (
                <div css={alertWrapperStyle}>
                  <AlertComponent type="danger">{props.errorMessage}</AlertComponent>
                </div>
              )}

              {props.isMapListingDataLoading &&
                <OverlayLoaderComponent spinnerStyle={props.processedItems.length >= 10 ? spinnerStyle : undefined} />
              }
            </OverlayWrapperComponent>
          </div>
        </ScrollBarComponent>
      ) : (
        <div css={emptyStyle}>
          <MobileEmptyMapListingComponent />
        </div>
      )}

      <RoundButtonComponent
        buttonStyle={RoundButtonStyle.ConfirmInverted}
        css={floatingCreateMapButtonStyle}
        icon={faSolidMapCirclePlus}
        onClick={props.onCreateMapButtonClick}
        size={RoundButtonSize.XLarge}
      />
    </div>
  );
};
